<template>
  <div class="row mb-3" v-if="activeTab === RETAILER_REGISTER_STEP.COMPANY_INFO">
    <div class="col-md-12 mb-1" :class="validationStatuses(v$.retailer.name)">
      <base-input
        :label="companyLabelText"
        v-model.trim="retailer.name"
        type="text"
        :class="validationStatuses(v$.retailer.name)"
        @blur="v$.retailer.name.$touch()"
        @focus="v$.retailer.name.$reset()"
        :invalid="v$.retailer.name.$error"
        :error-messages="retailerNameErrorMessages"
        tabindex="1"
        :autofocus="true"
        @click="formClickSendEvent"
      />
    </div>
    <div class="col-md-12 mb-1">
      <label class="font-14 input-label-spacing fw-500">{{ $t('views.retailer.users.register.company_type') }}</label>
      <base-multiselect
        v-model="retailer.company_type"
        :selectedLabel="$t('shared.forms.multiselect-selected-label-selected')"
        :hide-selected="true"
        placeholder=""
        label="name"
        track-by="id"
        :options="companyTypes"
        :multiple="false"
        :taggable="false"
        :class="validationStatuses(v$.retailer.company_type)"
        tabindex="2"
        @close="
          (value, id) => {
            v$.retailer.company_type.$touch()
            formClickSendEvent(value, id)
          }
        "
        @open="formClickSendEvent"
      ></base-multiselect>
      <template v-if="v$.retailer.company_type.$error">
        <div v-if="v$.retailer.company_type.required" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: $t('views.retailer.users.register.company_type')
            })
          }}
        </div>
      </template>
    </div>
    <div
      class="col-md-12 mb-1"
      :class="validationStatuses(v$.retailer.vkn)"
      v-if="retailer.company_type.id !== COMPANY_TYPE['SOLE_PROPRIETORSHIP']"
    >
      <base-input
        :label="vknTcknLabel"
        v-model.trim="retailer.vkn"
        type="text"
        pattern="\d*"
        :class="validationStatuses(v$.retailer.vkn)"
        @blur="v$.retailer.vkn.$touch()"
        @focus="v$.retailer.vkn.$reset()"
        :invalid="v$.retailer.vkn.$error"
        :error-messages="retailerVknErrorMessages"
        tabindex="3"
        @click="formClickSendEvent"
      />
    </div>
    <div
      class="col-md-12 mb-1"
      :class="validationStatuses(v$.retailer.tckn)"
      v-if="retailer.company_type.id === COMPANY_TYPE['SOLE_PROPRIETORSHIP']"
    >
      <base-input
        :label="vknTcknLabel"
        v-model.trim="retailer.tckn"
        type="number"
        pattern="\d*"
        :class="validationStatuses(v$.retailer.tckn)"
        @blur="v$.retailer.tckn.$touch()"
        @focus="v$.retailer.tckn.$reset()"
        :invalid="v$.retailer.tckn.$error"
        :error-messages="retailerTcknErrorMessages"
        tabindex="3"
        @click="formClickSendEvent"
      />
    </div>
    <div class="col-md-12 mb-1" v-if="isTaxNumberValidationRequired">
      <label class="font-14 input-label-spacing fw-500">{{
        $t('views.retailer.users.register.tax_administration_city')
      }}</label>
      <base-multiselect
        v-model="retailer.taxAdministrationCity"
        placeholder=""
        :selectedLabel="$t('shared.forms.multiselect-selected-label-selected')"
        :hide-selected="true"
        label="name"
        track-by="code"
        :options="cities"
        :multiple="false"
        :taggable="false"
        :loading="cityIsLoading"
        :disabled="cityIsLoading"
        :class="validationStatuses(v$.retailer.taxAdministrationCity)"
        tabindex="4"
        @select="$event => getTaxAdministrationOffices($event)"
        @close="
          (value, id) => {
            v$.retailer.taxAdministrationCity.$touch()
            formClickSendEvent(value, id)
          }
        "
        @open="formClickSendEvent"
      />
      <template v-if="v$.retailer.taxAdministrationCity.$error">
        <div v-if="v$.retailer.taxAdministrationCity.required" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: $t('views.retailer.users.register.tax_administration_city')
            })
          }}
        </div>
      </template>
    </div>
    <div class="col-md-12 mb-1" v-if="isTaxNumberValidationRequired">
      <label class="font-14 input-label-spacing fw-500">{{
        $t('views.retailer.users.register.tax_administration')
      }}</label>
      <base-multiselect
        v-model="retailer.taxAdministrationOffice"
        placeholder=""
        :selectedLabel="$t('shared.forms.multiselect-selected-label-selected')"
        :hide-selected="true"
        label="name"
        track-by="code"
        :options="taxAdministrationOffices"
        :multiple="false"
        :taggable="false"
        :loading="taxAdministrationOfficesAreLoading"
        :disabled="taxAdministrationOfficesAreLoading"
        :class="validationStatuses(v$.retailer.taxAdministrationOffice)"
        tabindex="5"
        @close="
          (value, id) => {
            v$.retailer.taxAdministrationOffice.$touch()
            formClickSendEvent(value, id)
          }
        "
        @open="formClickSendEvent"
      />
      <template v-if="v$.retailer.taxAdministrationOffice.$error">
        <div v-if="v$.retailer.taxAdministrationOffice.required" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: $t('views.retailer.users.register.tax_administration')
            })
          }}
        </div>
      </template>
    </div>
    <div class="col-md-12 mb-1">
      <label class="font-14 input-label-spacing fw-500">
        {{ $t('views.retailer.users.register.product_categories') }}
        <small class="fst-italic">({{ $t('views.retailer.users.register.product_categories_description') }})</small>
      </label>
      <base-multiselect
        v-model="selectedCategory"
        :selectedLabel="$t('shared.forms.multiselect-selected-label-selected')"
        :hide-selected="true"
        placeholder=""
        label="name"
        track-by="id"
        :options="categoriesByGroup"
        :multiple="true"
        :taggable="true"
        group-values="categories"
        group-label="name"
        :loading="categoryIsLoading"
        :disabled="categoryIsLoading"
        :class="validationStatuses(v$.retailer.categories)"
        tabindex="6"
        @close="
          (value, id) => {
            v$.retailer.categories.$touch()
            formClickSendEvent(value, id)
          }
        "
        @open="formClickSendEvent"
      ></base-multiselect>
      <template v-if="v$.retailer.categories.$error">
        <div v-if="v$.retailer.categories.required" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: $t('views.retailer.users.register.product_categories')
            })
          }}
        </div>
      </template>
    </div>
    <div class="col-md-12 mb-1">
      <label class="font-14 input-label-spacing fw-500">
        {{ $t('views.retailer.users.register.company_information') }}
        <small class="fst-italic">{{ $t('views.retailer.users.register.optional') }}</small>
      </label>
      <base-textarea
        v-model="retailer.details.information"
        type="text"
        :rows="3"
        tabindex="7"
        @click="formClickSendEvent"
      />
    </div>
    <div class="col-md-12 mb-1" :class="validationStatuses(v$.retailer.full_name)">
      <base-input
        :label="t('views.retailer.users.register.full_name')"
        v-model.trim="retailer.full_name"
        type="text"
        :class="validationStatuses(v$.retailer.full_name)"
        @blur="v$.retailer.full_name.$touch()"
        @focus="v$.retailer.full_name.$reset()"
        :invalid="v$.retailer.full_name.$error"
        :error-messages="retailerFullNameErrorMessages"
        tabindex="8"
        @click="formClickSendEvent"
      />
    </div>
    <div class="col-md-12 mb-1" @click="formClickSendEvent">
      <label class="font-14 input-label-spacing fw-500">
        {{ $t('views.retailer.users.register.birthdate') }}
        <small class="fst-italic">({{ $t('views.retailer.users.register.birthdate_description') }})</small>
      </label>
      <base-masked-input
        v-model.trim="retailer.birthdate"
        :mask="'##.##.####'"
        :placeholder="$t('views.retailer.applications.edit.birthdate_placeholder')"
        type="text"
        inputClass="form-control"
        :class="validationStatuses(v$.retailer.birthdate)"
        tabindex="9"
        @onMaskInputChange="blurBirthDateMaskedInput"
        @blur="handleBirthDateBlur"
      ></base-masked-input>
      <template v-if="v$.retailer.birthdate.$error">
        <div v-if="v$.retailer.birthdate.required.$invalid" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: t('views.retailer.users.register.birthdate')
            })
          }}
        </div>
        <div v-if="v$.retailer.birthdate.birthdate.$invalid" class="invalid-feedback">
          {{ $t('views.retailer.users.register.birthdate_error_text') }}
        </div>
      </template>
    </div>
    <div class="col-md-12 mb-1">
      <label class="font-14 input-label-spacing fw-500">{{ $t('views.retailer.users.register.city') }}</label>
      <base-multiselect
        v-model="city"
        :selectedLabel="$t('shared.forms.multiselect-selected-label-selected')"
        placeholder=""
        label="name"
        track-by="id"
        :options="cities"
        :multiple="false"
        :taggable="false"
        :deselectable="false"
        :hide-selected="true"
        @select="$event => getCounties($event)"
        :loading="cityIsLoading"
        :disabled="cityIsLoading"
        :class="validationStatuses(v$.retailer.city_id)"
        tabindex="10"
        @close="
          (value, id) => {
            v$.retailer.city_id.$touch()
            formClickSendEvent(value, id)
          }
        "
        @open="formClickSendEvent"
      ></base-multiselect>
      <template v-if="v$.retailer.city_id.$error">
        <div v-if="v$.retailer.city_id.required" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: $t('views.retailer.users.register.city')
            })
          }}
        </div>
      </template>
    </div>
    <div class="col-md-12 mb-1">
      <label class="font-14 input-label-spacing fw-500">{{ $t('views.retailer.users.register.county') }}</label>
      <base-multiselect
        v-model="county"
        :selectedLabel="$t('shared.forms.multiselect-selected-label-selected')"
        placeholder=""
        label="name"
        track-by="id"
        :options="counties"
        :hide-selected="true"
        :multiple="false"
        :taggable="false"
        :loading="countyIsLoading"
        :disabled="countyIsLoading"
        :class="validationStatuses(v$.retailer.county_id)"
        tabindex="11"
        @close="
          (value, id) => {
            v$.retailer.county_id.$touch()
            formClickSendEvent(value, id)
          }
        "
        @open="formClickSendEvent"
      ></base-multiselect>
      <template v-if="v$.retailer.county_id.$error">
        <div v-if="v$.retailer.county_id.required" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: $t('views.retailer.users.register.county')
            })
          }}
        </div>
      </template>
    </div>
    <div class="col-md-12 mb-1" :class="validationStatuses(v$.retailer.phone)" @click="formClickSendEvent">
      <label class="font-14 input-label-spacing fw-500">{{ $t('views.retailer.users.register.phone') }}</label>
      <base-masked-input
        v-model="retailer.phone"
        :mask="$t('shared.mask_formats.phone_mask')"
        :label="$t('views.retailer.users.register.phone_number')"
        type="tel"
        :inputClass="{
          ...validationStatuses(v$.retailer.phone),
          'form-control': true
        }"
        tabindex="12"
        @onMaskInputChange="blurPhoneMaskedInput"
        @blur="handlePhoneBlur"
        prefix="0"
        :ignoreLeadingZero="true"
      />
      <template v-if="!v$.retailer.phone.completed">
        <div v-if="v$.retailer.phone.$error && v$.retailer.phone.required" class="invalid-feedback">
          {{
            $t('shared.validations.required', {
              field: $t('views.retailer.users.register.phone_number')
            })
          }}
        </div>
        <div v-for="error of v$.retailer.phone.$errors" :key="error.$uid">
          <div class="invalid-feedback" v-if="error.$validator === 'minLength'">
            {{ $t('views.retailer.users.register.phone_number_error_text') }}
          </div>
        </div>
      </template>
    </div>
    <div class="col-md-12 mb-1">
      <base-input
        :label="t('views.retailer.users.register.email')"
        v-model.trim="retailer.email"
        type="email"
        :class="{
          error: v$.retailer.email.$error,
          dirty: v$.retailer.email.$dirty
        }"
        @blur="v$.retailer.email.$touch()"
        @focus="v$.retailer.email.$reset()"
        :invalid="v$.retailer.email.$error"
        :error-messages="retailerEmailErrorMessages"
        tabindex="13"
        @click="formClickSendEvent"
      />
    </div>
    <div class="col-md-12 mb-1">
      <base-input
        :label="t('views.retailer.users.register.password')"
        v-model="retailer.password"
        type="password"
        :class="validationStatuses(v$.retailer.password)"
        @focus="v$.retailer.password.$touch()"
        @blur="v$.retailer.password.$touch()"
        @change="delayTouch(v$.retailer.password)"
        tabindex="14"
        @click="formClickSendEvent"
      />
      <div v-if="v$.retailer.password.$error" class="card card-body mb-0 p-2">
        <ul class="text-danger ps-3 mb-0">
          <li
            :class="{
              isValid: v$.retailer.password.required.$response && v$.retailer.password.minLength.$response
            }"
          >
            {{ $t('shared.validations.minLengthPassword') }}
          </li>
          <li
            :class="{
              isValid: v$.retailer.password.containsNumber.$response
            }"
          >
            {{ $t('shared.validations.containsNumber') }}
          </li>
          <li
            :class="{
              isValid: v$.retailer.password.containsUppercase.$response
            }"
          >
            {{ $t('shared.validations.containsUppercase') }}
          </li>
          <li
            :class="{
              isValid: v$.retailer.password.containsLowercase.$response
            }"
          >
            {{ $t('shared.validations.containsLowercase') }}
          </li>
        </ul>
      </div>
    </div>
    <div class="col-md-12 mb-1">
      <base-input
        :label="t('views.retailer.users.register.password_repeat')"
        v-model="retailer.password_confirmation"
        type="password"
        :class="validationStatuses(v$.retailer.password_confirmation)"
        @focus="v$.retailer.password_confirmation.$touch()"
        @blur="v$.retailer.password_confirmation.$touch()"
        tabindex="15"
        @click="formClickSendEvent"
      />
      <template v-if="v$.retailer.password_confirmation.$error">
        <div v-for="error of v$.retailer.password_confirmation.$errors" :key="error.$uid">
          <div class="invalid-feedback" v-if="error.$validator === 'notSameAsCurrentPassword'">
            {{ $t('views.retailer.users.register.passwords_should_same') }}
          </div>
        </div>
      </template>
    </div>
    <div class="col-md-12 my-1">
      <base-checkbox
        :checked="false"
        v-model="retailer.confirmation"
        tabindex="16"
        @change="
          () => {
            $emit('privacy-policy-changed')
            formClickSendEvent()
          }
        "
      >
        <i18n-t keypath="views.retailer.users.register.privacy_policy">
          <template v-slot:privacy_policy_text>
            <a :href="$t('views.retailer.users.register.privacy_link')">
              {{ $t('views.retailer.users.register.privacy_policy_text') }}
            </a>
          </template>
        </i18n-t>
      </base-checkbox>
    </div>
  </div>
</template>
<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'

import { useStore } from 'vuex'

import { useVuelidate } from '@vuelidate/core'
import { email, helpers, maxLength, minLength, required, requiredIf, sameAs } from '@vuelidate/validators'
import { debounce, set } from 'lodash'
import { event as gEvent } from 'vue-gtag'

import useAddress from '@/composables/address.js'
import useValidation from '@/composables/validation.js'
import i18n from '@/i18n/i18n.js'

import CategoryService from '@/services/retailer/CategoryService.js'
import TaxNumberService from '@/services/retailer/TaxNumberService.js'
import { errorMessageWithParams } from '@/utilities/validationErrorMessage.js'

import { COMPANY_TYPE, COMPANY_TYPE_DATA } from '@/constants/companyType.js'
import { RETAILER_REGISTER_STEP } from '@/constants/retailerRegisterStep.js'
import { TRADE_TYPE_MAP } from '@/constants/tradeType.js'

import BaseCheckbox from '@/components/BaseCheckbox.vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseMaskedInput from '@/components/BaseMaskedInput.vue'
import BaseMultiselect from '@/components/BaseMultiselect.vue'
import BaseTextarea from '@/components/BaseTextarea.vue'

const { t } = i18n.global

// #region - touchmap init
const touchMap = new WeakMap()
// endregion

// #region - regex for validation rules
const fullName = helpers.regex(/^([A-Za-zşŞıİçÇöÖüÜĞğ ]{2,})+\s+([A-Za-zşŞıİçÇöÖüÜĞğ ]{2,})+$/)

const birthdate = helpers.regex(/^(0?[1-9]|[12][0-9]|3[01])[\.\-](0?[1-9]|1[012])[\.\-](19|20)\d{2}$/)
// endregion

// #region - store init
const store = useStore()
// endregion

// #region - props
const props = defineProps({
  activeTab: {
    type: Number,
    required: true
  }
})
// endregion

// #region - emit definitions
const emit = defineEmits(['privacy-policy-changed'])
// endregion

// #region - reactive state definitions
let retailer = reactive({
  vkn: '',
  tckn: '',
  taxAdministrationCity: null,
  taxAdministrationOffice: null,
  name: '',
  categories: null,
  city_id: null,
  county_id: null,
  details: {},
  password: '',
  password_confirmation: '',
  company_type: ''
})
// endregion

// #region - references
const categories = ref([])
let categoriesByGroup = reactive([])
const taxAdministrationOffices = ref([])
const categoryIsLoading = ref(false)
const taxAdministrationOfficesAreLoading = ref(false)
const selectedCategory = ref(null)
const isTaxNumberValidationRequired = ref(false)
const companyTypes = ref(COMPANY_TYPE_DATA)
// #endregion

// #region - Validation init
const { validationStatuses } = useValidation()
// endregion

// #region - Computed properties
const vknTcknLabel = computed(() => {
  if (retailer) {
    if (retailer.company_type === '') {
      return t('views.retailer.users.register.tckn_vkn')
    } else if (retailer.company_type.id === COMPANY_TYPE['SOLE_PROPRIETORSHIP']) {
      return t('views.retailer.users.register.tckn')
    }
    return t('views.retailer.users.register.tax_identification_number')
  }
  return t('views.retailer.users.register.tax_identification_number')
})

const retailerNameErrorMessages = computed(() =>
  errorMessageWithParams(v$.value.retailer.name, 'views.retailer.users.register.company_title')
)

const retailerVknErrorMessages = computed(() => errorMessageWithParams(v$.value.retailer.vkn, vknTcknLabel.value))

const retailerTcknErrorMessages = computed(() => errorMessageWithParams(v$.value.retailer.tckn, vknTcknLabel.value))

const retailerFullNameErrorMessages = computed(() =>
  errorMessageWithParams(v$.value.retailer.full_name, 'views.retailer.users.register.name')
)

const retailerEmailErrorMessages = computed(() =>
  errorMessageWithParams(v$.value.retailer.email, 'views.retailer.users.register.email')
)

const companyLabelText = computed(() => {
  const title = t('views.retailer.users.register.company_title')
  const desctiption = t('views.retailer.users.register.company_title_description')
  return `${title} <small class="fst-italic"> (${desctiption}) </small>`
})
// endregion

// #region - validation rule set
const rules = {
  retailer: {
    name: { required, $lazy: true },
    vkn: {
      required: requiredIf(() => {
        return retailer.company_type.id !== COMPANY_TYPE['SOLE_PROPRIETORSHIP']
      }),
      minLength: minLength(10),
      maxLength: maxLength(10),
      $lazy: true
    },
    tckn: {
      required: requiredIf(() => {
        return retailer.company_type.id === COMPANY_TYPE['SOLE_PROPRIETORSHIP']
      }),
      minLength: minLength(11),
      maxLength: maxLength(11),
      $lazy: true
    },
    taxAdministrationCity: {
      required: requiredIf(() => {
        return isTaxNumberValidationRequired.value
      })
    },
    taxAdministrationOffice: {
      required: requiredIf(() => {
        return isTaxNumberValidationRequired.value
      })
    },
    categories: { required },
    full_name: { required, fullName, $lazy: true },
    birthdate: { required, birthdate, $lazy: true },
    city_id: { required },
    county_id: { required },
    company_type: { required },
    phone: {
      required,
      minLength: () => {
        return minLength(t('shared.validation_rules.phone_min_length'))
      }
    },
    email: { required, email, $lazy: true },
    password: {
      required,
      minLength: minLength(10),
      containsUppercase: value => {
        return /[A-Z]/.test(value)
      },
      containsLowercase: value => {
        return /[a-z]/.test(value)
      },
      containsNumber: value => {
        return /[0-9]/.test(value)
      }
    },
    password_confirmation: {
      required,
      notSameAsCurrentPassword: value => value === retailer.password,
      minLength: minLength(10)
    },
    confirmation: {
      sameAs: sameAs(true)
    }
  }
}
// endregion

// #region - composable validation set
let v$ = useVuelidate(rules, { retailer })
// endregion

// #region - composable adress functions call
const { cities, counties, cityIsLoading, countyIsLoading, city, county, getCities, getCounties } = useAddress()
// endregion

// #region - onMounted
onMounted(() => {
  Promise.all([getCities(), getCategories(), checkTaxNumberValidation()])
})
// endregion

// #region - method definitions
const formClickSendEvent = () => {
  gEvent('form_actions', {
    event_action: 'form_clicked',
    form_name: 'Tarfin Yetkili Satış Noktası Kayıt'
  })
}

const handleBirthDateBlur = () => {
  v$.value.retailer.birthdate.$touch()
}

const blurBirthDateMaskedInput = detail => {
  v$.value.retailer.birthdate.$touch()
  v$.value.retailer.birthdate.completed = detail.completed
  if (detail.completed) {
    v$.value.retailer.birthdate.$model = detail.masked
  }
}

const handlePhoneBlur = () => {
  setTimeout(() => {
    v$.value.retailer.phone.$touch()
  }, 500)
}

const blurPhoneMaskedInput = detail => {
  v$.value.retailer.phone.$reset()
  v$.value.retailer.phone.completed = detail.completed
  if (detail.completed) {
    v$.value.retailer.phone.$model = detail.masked
  }
}

const delayTouch = v$ => {
  v$.$reset()
  if (touchMap.has(v$)) {
    clearTimeout(touchMap.get(v$))
  }
  touchMap.set(v$, setTimeout(v$.$touch, 250))
}

const checkTaxNumberValidation = () => {
  TaxNumberService.checkTaxNumberValidation()
    .then(response => {
      isTaxNumberValidationRequired.value = response.data
    })
    .catch(err => {
      throw err
    })
}

const getCategories = () => {
  categoryIsLoading.value = true
  CategoryService.getCategories().then(response => {
    categoryIsLoading.value = false
    categories.value = response.data
    categoriesByGroup = categoriesByGroupFunction(categories.value)
  })
}

const getTaxAdministrationOffices = city => {
  taxAdministrationOfficesAreLoading.value = true
  retailer.taxAdministrationOffice = null
  TaxNumberService.getTaxOfficesByCity(city.code)
    .then(response => {
      taxAdministrationOffices.value = response
    })
    .catch(err => {
      throw err
    })
    .finally(() => {
      taxAdministrationOfficesAreLoading.value = false
    })
}

const validateTaxNumber = () => {
  if (isTaxNumberValidationRequired.value) {
    TaxNumberService.validateTaxNumber(retailer.taxAdministrationOffice.code, retailer.vkn)
      .then(response => {
        if (response.data.status) {
          nextTabDisabled.value = false
          retailer.name = response.data.company_title
        } else {
          nextTabDisabled.value = true
          retailer.name = ''
          const notifications = {
            type: 'danger',
            message: t('views.retailer.users.register.tax_identification_not_verified')
          }
          store.dispatch('notification/add', notifications, {
            root: true
          })
        }
      })
      .catch(err => {
        nextTabDisabled.value = true
        retailer.name = ''
        const notifications = {
          type: 'danger',
          message: t('views.retailer.users.register.tax_identification_not_verified') + errorMessage(err)
        }
        store.dispatch('notification/add', notifications, {
          root: true
        })
      })
  }
}

const validateRetailer = () => {
  v$.value.retailer.$touch()
  if (!v$.value.retailer.$error) {
    return true
  }
  return false
}

const minMaxValueControl = error => {
  return error.$validator === 'minLength' || error.$validator === 'maxLength'
}
// endregion

// #region - watch part
watch(
  () => selectedCategory.value,
  newCategory => {
    retailer.categories = newCategory.map(item => item.id)
  }
)

watch(
  () => city.value,
  newCity => {
    retailer.city_id = newCity.id
  }
)

watch(
  () => county.value,
  newCounty => {
    retailer.county_id = newCounty.id
  }
)

watch(
  () => retailer.vkn,
  debounce(newVkn => {
    if (newVkn && retailer.taxAdministrationCity && retailer.taxAdministrationOffice) {
      validateTaxNumber()
    }
  }, 1000)
)

watch(
  () => retailer.taxAdministrationCity,
  newTaxCity => {
    if (newTaxCity && retailer.vkn && retailer.taxAdministrationOffice) {
      validateTaxNumber()
    }
  }
)

watch(
  () => retailer.taxAdministrationOffice,
  newOffice => {
    if (newOffice && retailer.vkn && retailer.taxAdministrationCity) {
      validateTaxNumber()
    }
  }
)

watch(
  () => retailer.company_type,
  newCompanyType => {
    set(retailer, 'company_type', newCompanyType)
    retailer.tckn = ''
    retailer.vkn = ''
  }
)

watch(
  () => categories.value,
  (newValue, oldValue) => {
    categoriesByGroup = categoriesByGroupFunction(newValue)
  }
)
// endregion

// #region - method definition that reducing category for multiselect optionss
const categoriesByGroupFunction = categoriesParam => {
  const groupedCategoryObject = categoriesParam.reduce((acc, curr) => {
    if (acc[curr.trade_type]) {
      acc[curr.trade_type].categories.push(curr)
    } else {
      acc[curr.trade_type] = {
        name: TRADE_TYPE_MAP[curr.trade_type],
        categories: [curr]
      }
    }
    return acc
  }, {})
  return Object.values(groupedCategoryObject)
}
// endregion

// #region - defineExpose to expose properties to be accessible from the parent
defineExpose({
  v$,
  retailer
})
// endregion
</script>
<style lang="scss" scoped>
.isValid {
  color: $primary;
  text-decoration: line-through;
}
</style>
